import {
  arrow,
  autoUpdate,
  flip,
  FloatingArrow,
  offset,
  type Placement,
  size,
  type Strategy,
  useClick,
  useFloating,
  useInteractions
} from '@floating-ui/react';
import { cloneElement, ComponentProps, JSX, useMemo, useRef } from 'react';
import { SPACE_4 } from '../../tokens';

type Args = {
  anchor: JSX.Element;
  arrowColor?: ComponentProps<typeof FloatingArrow>['fill'];
  placement: Placement;
  strategy?: Strategy;
};

const usePopover = ({ anchor, arrowColor, placement, strategy }: Args) => {
  const arrowRef = useRef<SVGSVGElement>(null);

  const { refs, floatingStyles, context } = useFloating({
    placement,
    strategy,
    middleware: [
      flip(),
      offset({ mainAxis: SPACE_4, crossAxis: 0 }),
      arrowColor ? arrow({ element: arrowRef }) : undefined,
      // Publishes the vertical space between the popover and the viewport edge (on the side flip()
      // picked, kept fresh by autoUpdate) as a CSS variable. Popover content can opt into a
      // viewport-aware height with `max-height: var(--popover-available-height); overflow-y: auto;`
      // — without it, tall content overflows the viewport in small windows/iframes.
      size({
        apply({ availableHeight, elements }) {
          elements.floating.style.setProperty('--popover-available-height', `${Math.max(0, availableHeight)}px`);
        },
        padding: SPACE_4
      })
    ],
    whileElementsMounted: autoUpdate
  });

  const anchorElement = useMemo(() => cloneElement(anchor, { ref: refs.setReference }), [anchor, refs]);
  const popoverRef = refs.setFloating;
  const floatingRef = refs.floating;

  const click = useClick(context);
  const { getFloatingProps } = useInteractions([click]);

  const arrowElement = useMemo(
    () => (arrowColor ? <FloatingArrow ref={arrowRef} context={context} fill={arrowColor} /> : null),
    [arrowRef, context, arrowColor]
  );

  return useMemo(
    () => ({
      anchorElement,
      arrowElement,
      floatingRef,
      popoverInteractiveProps: getFloatingProps(),
      popoverRef,
      popoverStyle: floatingStyles
    }),
    [anchorElement, arrowElement, floatingRef, getFloatingProps, floatingStyles, popoverRef]
  );
};

export default usePopover;
